/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file Connectors App and Organization Management constants
 * @module Epic.AppOrchard.ConnectorManagement.Constants
 */

import { IConnectorApp } from "ao/types/connectorManagement";

export enum ManagementRecordType {
	App = 0,
	Organization = 1,
}

export enum ManagementTabs {
	Apps = 0,
	Organizations = 1,
}

export enum ConnectorManagementActions {
	Save = "Save",
	Delete = "Delete",
	Restore = "Restore",
	Merge = "Merge",
}

export enum SharedColumnKeys {
	id = "id",
	trackId = "trackId",
	name = "name",
	reviewStatus = "reviewStatus",
	reviewer = "reviewer",
	reviewedOn = "reviewedOn",
	comment = "comment",
	actionLinks = "actionLinks",
}

export enum AppListColumnKeys {
	clientAppId = "clientAppId",
	appCategories = "appCategories",
	version = "version",
}

export enum OrgListColumnKeys {
	organizationId = "organizationId",
	type = "type",
	website = "website",
}

export type ConnectorManagementFilterColumnMap = Record<string, string>;

export const AppListFilterColumnMapping: ConnectorManagementFilterColumnMap = {
	id: SharedColumnKeys.name,
	appCategories: AppListColumnKeys.appCategories,
	reviewStatus: SharedColumnKeys.reviewStatus,
	reviewer: SharedColumnKeys.reviewer,
};

export const OrgListFIlterColumnMappings: ConnectorManagementFilterColumnMap = {
	id: SharedColumnKeys.name,
	type: OrgListColumnKeys.type,
	reviewStatus: SharedColumnKeys.reviewStatus,
	reviewer: SharedColumnKeys.reviewer,
};

export enum ReviewStatus {
	NotSubmitted = 1,
	WaitingForReview = 2,
	InReview = 3,
	Approved = 4,
	Rejected = 5,
	Merged = 6,
}

export enum MergeRecord {
	Source = "source",
	Target = "target",
	Result = "result",
}

export type AppMergeColumnTitle = MergeRecord | "header" | "selection" | "merge";

export enum AppMergeRowTitle {
	Header = "header",
	SubHeader = "subheader",
	Tags = "Tags",
	Metadata = "Metadata",
	ReviewStatus = "Review Status",
	Reviewer = "Reviewer",
	Comment = "Comment",
	Aliases = "Aliases",
	Categories = "Categories",
	VendorCategories = "Vendor Reported Categories",
	Engine = "Engine",
}

export const EmptyConnectorApp: IConnectorApp = {
	id: 0,
	name: "",
	comment: "",
	isEngine: false,
	categories: [],
	aliases: [],
	reviewStatus: { id: 0, name: "" },
	reviewer: undefined,
	trackId: "",
	clientAppId: 0,
	vendor: {
		id: 0,
		trackId: "",
		name: "",
	},
	isPrivateApp: false,
	lastUpdatedOn: "",
	lastUpdatedBy: undefined,
	isDeleted: false,
	isEpicOnFHIR: false,
	reviewedOn: "",
	addedOn: "",
};

export const Colors = ["orange", "green", "purple", "pink", "blue"];

export const MaxAppsToReturn = 10;

export const SuccessToastVerbs: {
	[key in ConnectorManagementActions]: string;
} = {
	Save: "saved",
	Delete: "deleted",
	Restore: "restored",
	Merge: "merged",
};

export const SuccessToastTitles: {
	[key in ConnectorManagementActions]: string;
} = {
	Save: "Changes Saved",
	Delete: "Record Deleted",
	Restore: "Record Restored",
	Merge: "Records Merged",
};

export const FailureToastTitles: {
	[key in ConnectorManagementActions]: string;
} = {
	Save: "Save Error",
	Delete: "Delete Error",
	Restore: "Restore Error",
	Merge: "Merge Error",
};

export const connectorAppAliasFieldTitles = {
	aliases: "Alternative names",
	alias: "An alternative name",
	comment: "Context for the alias",
};

export const connectorOrganizationAppsListFieldTitles = {
	id: "ID used in Connectors",
	name: "Name used in Connectors",
	app: "App owned or managed by the organization",
	apps: "Apps owned or managed by the organization",
	categories: "Categories assigned to the app by admin",
};

export const vendorReportedDataTitle = "Data sourced from outside Connectors Apps cannot be changed";

export const AliasForDefaultNameId: number = -1;

export const editSpacingY: number = 6;
export const editSpacingX: number = 4;

export const InterfaceEngineCategoryName: string = "Interface Engine";

export const MaxCommentLength: number = 3000;
export const MaxDeletionCommentLength: number = 80;
export const AppendCommentBuffer: number = 2;
